import { Notification } from '../../shared/entities/notification/notification';

/**
 * Eine Tagesgruppe im Popover — der Kopf „HEUTE" / „GESTERN" / „Mittwoch, 12.08." und die Zeilen
 * darunter.
 *
 * Entweder `labelKey` ODER `labelDate` ist gesetzt, nie beides: die beiden jungen Tage haben eine
 * eigene Benennung und damit einen i18n-Key, aeltere tragen ihr Datum und werden im Template mit
 * der `date`-Pipe formatiert. Die Formatierung gehoert nicht hierher — diese Funktion soll ohne
 * Angular-Locale testbar bleiben.
 */
export interface NotificationDayGroup {
  labelKey: string | null;
  labelDate: string | null;
  items: Notification[];
}

const TODAY_KEY = 'notificationCenterComponent.groups.today';
const YESTERDAY_KEY = 'notificationCenterComponent.groups.yesterday';

/** Kalendertag in Ortszeit. Bewusst nicht per UTC: die Tagesgrenze ist die des Nutzers. */
function dayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

/**
 * Gruppiert den Stream nach Kalendertagen (#736-Nacharbeit 19.08.2026).
 *
 * Gruppiert werden AUFEINANDERFOLGENDE Zeilen desselben Tages, nicht alle Zeilen eines Tages. Der
 * Server liefert absteigend sortiert, beides ist damit dasselbe Ergebnis — aber die Liste wird so
 * unter keinen Umstaenden umsortiert. Eine Liste, die der Nutzer scrollt und nachlaedt, darf ihre
 * Reihenfolge nicht vom Client geaendert bekommen.
 *
 * `now` ist ein Parameter und kein `new Date()` im Rumpf, damit die Funktion ohne gestellte
 * Systemzeit testbar ist.
 */
export function groupByDay(items: Notification[], now: Date = new Date()): NotificationDayGroup[] {
  const todayKey = dayKey(now);
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  const yesterdayKey = dayKey(yesterday);

  const groups: NotificationDayGroup[] = [];
  let currentKey: string | null = null;

  for (const item of items) {
    const created = new Date(item.createdAt);
    // Ein unlesbarer Zeitstempel ergibt "NaN-NaN-NaN" — eine eigene, namenlose Gruppe. Die Zeile
    // faellt dadurch nicht aus der Liste, und der Kopf bleibt leer statt "Invalid Date".
    const key = dayKey(created);

    if (key !== currentKey) {
      currentKey = key;
      groups.push({
        labelKey: key === todayKey ? TODAY_KEY : key === yesterdayKey ? YESTERDAY_KEY : null,
        labelDate: key === todayKey || key === yesterdayKey ? null : item.createdAt,
        items: []
      });
    }

    groups[groups.length - 1].items.push(item);
  }

  return groups;
}
